import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, brl, errToast } from "@/lib/api";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Minus, Trash2, Clock, Users, Receipt, X, Search, MessageCircle, Printer, ShoppingCart, Lock, Split, Pencil, MessageSquare, WifiOff, RefreshCw, FileText, Loader2, CheckCircle2, AlertTriangle, Merge } from "lucide-react";
import DailyGoalWidget from "@/components/DailyGoalWidget";
import ConferenciaDialog from "@/components/ConferenciaDialog";
import { useConnectivity, useTabSyncQueue } from "@/lib/connectivity";
import useLivePolling from "@/hooks/useLivePolling";
import { fetchNfceBySale, openNfceCupom, NFCE_IMPRIMIVEL } from "@/lib/nfceService";
import {
  saveTab, getTab, getAllTabs, getCachedProducts, getCachedCategories, getCachedCustomers,
  queueTabMutation, getTabMutations,
} from "@/lib/offline_db";
import { isQZAvailable, getBranchPrinter, printReceipt as qzPrintReceipt } from "@/lib/qzTray";

function newUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// Teto da gorjeta, em pontos percentuais. Espelha GORJETA_TETO_PERCENTUAL de
// backend/tip_lib.py — o backend recusa acima disso; a tela evita chegar lá.
const GORJETA_TETO_PCT = 10;

// Comandeiras de um produto ou item da comanda: a lista manda; o campo de um
// destino só é o legado (mesma regra de tabs.product_print_destinations).
function printDests(x) {
  const list = (Array.isArray(x?.print_destinations) && x.print_destinations.length)
    ? x.print_destinations
    : [x?.print_destination || "cozinha"];
  return [...new Set(list.map(d => String(d || "").toLowerCase()).filter(Boolean))];
}

export default function Tabs() {
  const [tabs, setTabs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ label:"", party_size:1, customer_id:"", notes:"" });
  const [selectedTab, setSelectedTab] = useState(null); // currently open tab dialog
  const [tick, setTick] = useState(0); // for live timer
  const [fiscalConfig, setFiscalConfig] = useState(null);
  const tabsUserRole = (JSON.parse(localStorage.getItem("oplyx_user") || "{}"))?.role || "owner";
  const [tipDefaultPercent, setTipDefaultPercent] = useState(10);

  const conn = useConnectivity();
  const tabSync = useTabSyncQueue(conn.online);

  const load = async () => {
    if (!conn.online) {
      try {
        const [ts, cu, p, cat] = await Promise.all([
          getAllTabs(), getCachedCustomers(), getCachedProducts(), getCachedCategories(),
        ]);
        setTabs((ts || []).filter(t => ["open","partial","awaiting_cashier"].includes(t.status)));
        setCustomers(cu || []);
        setProducts((p || []).filter(x => x.active !== false));
        setCats(cat || []);
      } catch (err) { console.error("[tabs] idb load falhou:", err); }
      return;
    }
    return Promise.all([
      api.get("/tabs?status=open"),
      api.get("/customers"),
      api.get("/products"),
      api.get("/categories"),
    ]).then(([a,c,p,cat])=>{
      setTabs(a.data); setCustomers(c.data);
      setProducts(p.data.filter(x=>x.active!==false)); setCats(cat.data);
    }).catch(async (err) => {
      console.error("[tabs] load falhou:", err);
      // Fallback to IDB
      try {
        const [ts, cu, p, cat] = await Promise.all([
          getAllTabs(), getCachedCustomers(), getCachedProducts(), getCachedCategories(),
        ]);
        setTabs((ts || []).filter(t => ["open","partial","awaiting_cashier"].includes(t.status)));
        setCustomers(cu || []); setProducts((p||[]).filter(x=>x.active!==false)); setCats(cat||[]);
        toast.warning("Carregando comandas do cache local (sem conexão).");
      } catch { toast.error("Erro ao carregar comandas."); }
    });
  };

  useEffect(()=>{ load(); },[conn.online]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mantém a lista igual à dos outros terminais e da web. Só busca as comandas
  // (clientes/produtos não mudam a cada venda) e pausa com mutação offline pendente.
  useLivePolling(async () => {
    const { data } = await api.get("/tabs?status=open");
    setTabs(data);
  }, { intervalMs: 2000, enabled: conn.online && !tabSync.syncing && tabSync.pendingCount === 0 });
  useEffect(()=>{ const i = setInterval(()=>setTick(t=>t+1), 30000); return ()=>clearInterval(i); },[]);
  useEffect(()=>{
    api.get("/fiscal/companies").then(({ data: co }) => {
      // Módulo fiscal ativo: acompanha e imprime a NFC-e das vendas.
      setFiscalConfig({ nfce_disponivel: co.nfce_disponivel ?? false });
    }).catch(()=>{});
  }, []);
  useEffect(()=>{
    api.get("/auth/me").then(({ data }) => {
      const pct = data?.settings?.tip_default_percent;
      if (pct != null) setTipDefaultPercent(parseFloat(pct) || 10);
    }).catch(()=>{});
  }, []);

  const openTab = async (e) => {
    e.preventDefault();
    if (!conn.online) {
      const offlineId = `offline_${newUuid().replace(/-/g, "").slice(0, 8)}`;
      const tabPayload = { ...form, customer_id: form.customer_id || null };
      const newTab = {
        id: offlineId,
        ...tabPayload,
        items: [],
        total: 0,
        status: "open",
        opened_at: new Date().toISOString(),
        _offline_created: true,
      };
      try {
        await saveTab(newTab);
        await queueTabMutation({
          _mutation_id: newUuid(),
          tab_id: offlineId,
          type: "create_tab",
          payload: tabPayload,
        });
        toast.success(`Comanda "${form.label}" criada offline. Sincroniza quando a rede voltar`);
        setOpenNew(false); setForm({ label:"", party_size:1, customer_id:"", notes:"" });
        setTabs(prev => [...prev, newTab]);
        tabSync.refreshCount();
        // Already opens the tab for adding products — no need to click it again.
        setSelectedTab(newTab);
      } catch (err) { toast.error("Erro ao criar comanda offline: " + (err?.message || err)); }
      return;
    }
    try {
      const { data } = await api.post("/tabs", { ...form, customer_id: form.customer_id || null });
      toast.success(`Comanda "${form.label}" aberta`);
      setOpenNew(false); setForm({ label:"", party_size:1, customer_id:"", notes:"" });
      load();
      // Already opens the tab for adding products — no need to click it again.
      setSelectedTab(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const elapsedStr = (iso) => {
    const diff = Math.floor((Date.now() - new Date(iso).getTime())/60000);
    if (diff < 60) return `${diff}min`;
    const h = Math.floor(diff/60), m = diff%60;
    return `${h}h${String(m).padStart(2,"0")}`;
  };

  return (
    <div className="space-y-6" data-testid="tabs-page">
      {(!conn.online || tabSync.pendingCount > 0) && (
        <div
          className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border text-sm ${
            !conn.online
              ? "bg-red-50 border-red-200 text-red-900"
              : "bg-amber-50 border-amber-200 text-amber-900"
          }`}
          data-testid="tabs-offline-banner"
        >
          <div className="flex items-center gap-2">
            <WifiOff className="w-4 h-4 shrink-0" />
            <span className="font-semibold">
              {!conn.online ? "Offline" : "Sincronizando"}
            </span>
            {!conn.online && (
              <span className="text-[12px] opacity-90">
                · novas comandas e itens ficam na fila até a rede voltar
              </span>
            )}
          </div>
          {tabSync.pendingCount > 0 && (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-100 border border-amber-300 text-amber-900 rounded-full font-semibold text-[12px]"
              data-testid="tabs-pending-count"
            >
              {tabSync.syncing && <RefreshCw className="w-3 h-3 animate-spin" />}
              {tabSync.pendingCount} mutação{tabSync.pendingCount > 1 ? "ões" : ""} pendente{tabSync.pendingCount > 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Comandas</h1>
          <p className="text-sm text-muted-foreground">Abra uma comanda por mesa/nome e adicione consumo até o fechamento</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DailyGoalWidget compact />
          <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-tab-btn"><Plus className="w-4 h-4 mr-1"/>Nova comanda</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Abrir comanda</DialogTitle></DialogHeader>
            <form onSubmit={openTab} className="space-y-3">
              <div><Label>Rótulo (mesa / nome / número)</Label>
                <Input required placeholder="Mesa 5 • João • #12" value={form.label} onChange={e=>setForm({...form,label:e.target.value})} data-testid="tab-label" autoFocus />
              </div>
              <div><Label>Número de pessoas</Label>
                <Input required type="number" min="1" max="50" value={form.party_size} onChange={e=>setForm({...form,party_size:parseInt(e.target.value)||1})} data-testid="tab-party" />
              </div>
              <div><Label>Cliente (opcional)</Label>
                <Select value={form.customer_id || "none"} onValueChange={v=>setForm({...form,customer_id:v==="none"?"":v})}>
                  <SelectTrigger data-testid="tab-customer"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem cliente vinculado</SelectItem>
                    {customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name} ({c.phone})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Observações</Label>
                <Input value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="(opcional)" />
              </div>
              <Button type="submit" className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="tab-save">Abrir comanda</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3" data-testid="open-tabs-list">
        {tabs.map(t => (
          <Card key={t.id} className={`border-neutral-200 hover:border-emerald-400 cursor-pointer transition ${
            t.status === "awaiting_cashier" ? "ring-2 ring-amber-500 bg-amber-100/50" :
            t.close_requested_at ? "ring-2 ring-amber-400 bg-amber-50/40" :
            t.attention_at ? "ring-2 ring-rose-400 bg-rose-50/30" : ""
          }`} onClick={()=>setSelectedTab(t)} data-testid={`tab-card-${t.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-bold text-lg flex items-center gap-1.5 flex-wrap">
                    {t.label}
                    {t._offline_created && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200"
                            data-testid={`tab-offline-${t.id}`}>
                        🔴 offline
                      </span>
                    )}
                    {t.status === "awaiting_cashier" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500 text-white font-semibold"
                            title={`Garçom fechou, total ${t.final_total ? `${brl(t.final_total)}` : ""}`}
                            data-testid={`tab-awaiting-${t.id}`}>
                        ⏳ aguard. caixa
                      </span>
                    )}
                    {t.attention_at && t.status !== "awaiting_cashier" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-600 text-white"
                            title="Garçom solicitou atenção"
                            data-testid={`tab-attention-${t.id}`}>
                        ⚠ atenção
                      </span>
                    )}
                    {t.close_requested_at && t.status !== "awaiting_cashier" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-600 text-white"
                            title={`Cliente pediu a conta ${(t.close_requested_at || "").slice(11,16)}`}
                            data-testid={`tab-closing-${t.id}`}>
                        🕒 conta pedida
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3"/>{t.party_size}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3"/>{elapsedStr(t.opened_at)}</span>
                    <span>• {t.items?.length || 0} iten{(t.items?.length||0)===1?"":"s"}</span>
                  </div>
                </div>
                <TabStatusBadge status={t.status || "open"} />
              </div>
              <div className="text-2xl font-bold tracking-tight">
                {brl(t.final_total || t.total)}
                {t.tip_total > 0 && (
                  <span className="text-xs font-normal text-emerald-700 ml-2">
                    (+{brl(t.tip_total)} gorjeta)
                  </span>
                )}
              </div>
              {(t.paid_amount || 0) > 0 && (
                <div className="text-xs mt-1 flex items-center justify-between" data-testid={`tab-progress-${t.id}`}>
                  <span className="text-emerald-700">Pago: <b>{brl(t.paid_amount)}</b></span>
                  <span className="text-amber-700">Falta: <b>{brl(t.remaining_amount || 0)}</b></span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {tabs.length === 0 && (
          <Card className="md:col-span-3 border-dashed"><CardContent className="p-10 text-center text-muted-foreground">
            Nenhuma comanda aberta. Clique em <b>Nova comanda</b> para iniciar.
          </CardContent></Card>
        )}
      </div>

      {selectedTab && (
        <TabDetails tabId={selectedTab.id} products={products} cats={cats} customers={customers}
                     offline={!conn.online} fiscalConfig={fiscalConfig} userRole={tabsUserRole}
                     tipDefaultPercent={tipDefaultPercent}
                     onClose={()=>{ setSelectedTab(null); load(); }} />
      )}
    </div>
  );
}

function TabDetails({ tabId, products, cats, customers, offline, fiscalConfig, userRole = "owner", tipDefaultPercent = 10, onClose }) {
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [catFilter, setCatFilter] = useState("all");
  const [q, setQ] = useState("");
  const [cashOpen, setCashOpen] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [conferenciaOpen, setConferenciaOpen] = useState(false);
  const [savingParty, setSavingParty] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState("");
  const [savingLabel, setSavingLabel] = useState(false);
  // Iter 90 — diálogo de personalização ao adicionar item
  const [customizeFor, setCustomizeFor] = useState(null);   // produto sendo customizado

  const isOfflineId = typeof tabId === "string" && tabId.startsWith("offline_");

  const load = () => {
    if (isOfflineId || offline) {
      return getTab(tabId).then(tab => { if (tab) setT(tab); }).catch(() => {});
    }
    return api.get(`/tabs/${tabId}`).then(r=>setT(r.data)).catch(() => {
      getTab(tabId).then(tab => { if (tab) setT(tab); }).catch(() => {});
    });
  };
  const loadCash = () => api.get("/financial/cash/current")
    .then(r => setCashOpen(r.data?.status === "open"))
    .catch(() => setCashOpen(false));
  useEffect(()=>{ load(); loadCash(); },[tabId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click em QUALQUER produto sempre abre o diálogo — garante que o garçom seja
  // sempre questionado sobre observação no momento do pedido com o cliente.
  const onProductClick = (p) => {
    setCustomizeFor(p);
  };
  const addCustomized = async (payload) => {
    if (offline || isOfflineId) {
      // Offline: add item locally and queue mutation
      const product = products.find(p => p.id === payload.product_id);
      const deltaPerUnit = (payload.modifiers || []).reduce(
        (s, m) => s + (parseFloat(m.price_delta) || 0), 0
      );
      const unitPrice = (parseFloat(product?.price) || 0) + deltaPerUnit;
      const subtotal = +(unitPrice * (parseFloat(payload.quantity) || 1)).toFixed(2);
      const newItem = {
        id: `offline_item_${newUuid().replace(/-/g,"").slice(0, 8)}`,
        product_id: payload.product_id,
        name: product?.name || "Produto",
        quantity: parseFloat(payload.quantity) || 1,
        unit_price: unitPrice,
        price: parseFloat(product?.price) || 0,
        subtotal,
        modifiers: payload.modifiers || [],
        notes: payload.notes || null,
        added_at: new Date().toISOString(),
        print_status: product?.no_print ? "none" : "pending",
        paid: false,
        _offline: true,
      };
      const updatedTab = {
        ...t,
        items: [...(t?.items || []), newItem],
        total: +((t?.total || 0) + subtotal).toFixed(2),
      };
      try {
        await saveTab(updatedTab);
        await queueTabMutation({
          _mutation_id: newUuid(),
          tab_id: tabId,
          type: "add_items",
          payload: [payload],
        });
        setT(updatedTab);
        setCustomizeFor(null);
      } catch (e) {
        toast.error("Erro ao salvar item offline: " + (e?.message || e));
      }
      return;
    }
    try {
      await api.post(`/tabs/${tabId}/items`, [payload]);
      setCustomizeFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao adicionar item");
    }
  };

  const sendToPrint = async () => {
    try {
      const { data } = await api.post(`/tabs/${tabId}/send-to-print`);
      if (!data.sent) {
        toast.info("Nenhum item pendente de impressão.");
        return;
      }
      const summary = (data.jobs || []).map(j => `${j.items.reduce((s, x) => s + (x.quantity || 0), 0)} → ${j.destination}`).join(" · ");
      toast.success(`Enviado para impressão: ${summary}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar para impressão");
    }
  };
  const removeItem = async (itemId) => {
    await api.delete(`/tabs/${tabId}/items/${itemId}`);
    load();
  };

  const updateParty = async (delta) => {
    if (!t) return;
    const next = Math.max(1, Math.min(50, (t.party_size || 1) + delta));
    if (next === t.party_size) return;
    setSavingParty(true);
    // optimistic update for instant mobile feedback
    setT({ ...t, party_size: next });
    try {
      await api.put(`/tabs/${tabId}`, {
        label: t.label,
        party_size: next,
        customer_id: t.customer_id || null,
        notes: t.notes || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar nº de pessoas");
      load(); // revert
    } finally {
      setSavingParty(false);
    }
  };

  const renameLabel = async () => {
    const next = labelDraft.trim();
    if (!next || next === t.label) { setEditingLabel(false); return; }
    setSavingLabel(true);
    try {
      await api.put(`/tabs/${tabId}`, {
        label: next,
        party_size: t.party_size || 1,
        customer_id: t.customer_id || null,
        notes: t.notes || "",
      });
      setT({ ...t, label: next });
      setEditingLabel(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao renomear comanda");
    } finally {
      setSavingLabel(false);
    }
  };

  const confirmCancel = async () => {
    setCancelling(true);
    try {
      await api.post(`/tabs/${tabId}/cancel`, { reason: cancelReason.trim() });
      toast.success("Comanda cancelada");
      setCancelOpen(false);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setCancelling(false); }
  };

  const goToCash = () => {
    if (!t?.items?.length) {
      toast.warning("Adicione itens antes de finalizar");
      return;
    }
    // Iter 129 — POS Standalone fix: stay inside the standalone window if we
    // are running there, instead of navigating to /pos (which would open the
    // full AppShell on a different route and break the offline-first flow).
    const isStandalone =
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/pos/standalone");
    const posPath = isStandalone ? "/pos/standalone" : "/pos";
    if (!cashOpen) {
      toast.error("Abra o caixa antes de finalizar a comanda");
      nav(`${posPath}?open-cash=1`);
      return;
    }
    // Navigate to POS with tab pre-loaded
    onClose();
    nav(`${posPath}?tab=${tabId}`);
  };

  if (!t) return null;
  // Só categorias com produto ativo, com a contagem para achar rápido.
  const catChips = cats
    .map(c => ({ id: c.id, name: c.name, count: products.filter(p => p.category_id === c.id).length }))
    .filter(c => c.count > 0);
  const filtered = products.filter(p=>(catFilter==="all"||p.category_id===catFilter) && (!q || p.name.toLowerCase().includes(q.toLowerCase())));

  return (
    <Dialog open={true} onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto md:overflow-hidden flex flex-col" data-testid="tab-details-dialog">
        <DialogHeader>
              <DialogTitle className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  {editingLabel ? (
                    <div className="flex items-center gap-1.5" data-testid="tab-label-editor">
                      <Input
                        autoFocus
                        value={labelDraft}
                        onChange={e => setLabelDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); renameLabel(); } if (e.key === "Escape") setEditingLabel(false); }}
                        className="h-8 text-base font-semibold w-48"
                        data-testid="tab-label-input"
                      />
                      <Button size="sm" onClick={renameLabel} disabled={savingLabel} className="h-8 px-2" data-testid="tab-label-save">
                        {savingLabel ? <Loader2 className="w-3 h-3 animate-spin"/> : "Salvar"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingLabel(false)} className="h-8 px-2" data-testid="tab-label-cancel">Cancelar</Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <div className="text-xl truncate">{t.label}</div>
                      {t.status === "open" && (
                        <button
                          type="button"
                          onClick={() => { setLabelDraft(t.label); setEditingLabel(true); }}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          aria-label="Renomear comanda"
                          data-testid="tab-label-edit-btn"
                        >
                          <Pencil className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground font-normal flex items-center gap-2 mt-1 flex-wrap">
                    <span className="inline-flex items-center gap-1" data-testid="tab-party-editor">
                      <Users className="w-3 h-3"/>
                      <button
                        type="button"
                        onClick={()=>updateParty(-1)}
                        disabled={savingParty || (t.party_size||1) <= 1}
                        className="w-7 h-7 rounded-full border border-neutral-300 bg-white hover:bg-neutral-50 disabled:opacity-40 flex items-center justify-center"
                        aria-label="Reduzir pessoas"
                        data-testid="tab-party-minus">
                        <Minus className="w-3 h-3"/>
                      </button>
                      <span className="font-semibold tabular-nums min-w-[2ch] text-center" data-testid="tab-party-count">{t.party_size}</span>
                      <button
                        type="button"
                        onClick={()=>updateParty(1)}
                        disabled={savingParty || (t.party_size||1) >= 50}
                        className="w-7 h-7 rounded-full border border-neutral-300 bg-white hover:bg-neutral-50 disabled:opacity-40 flex items-center justify-center"
                        aria-label="Aumentar pessoas"
                        data-testid="tab-party-plus">
                        <Plus className="w-3 h-3"/>
                      </button>
                      <span>pessoa{t.party_size===1?"":"s"}</span>
                    </span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{Math.floor((Date.now()-new Date(t.opened_at).getTime())/60000)}min aberta</span>
                    {t.customer && <span>• {t.customer.name}</span>}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={()=>{ setCancelReason(""); setCancelOpen(true); }} className="text-rose-600" data-testid="tab-cancel"><X className="w-4 h-4 mr-1"/>Cancelar</Button>
              </DialogTitle>
            </DialogHeader>

            <div className="grid md:grid-cols-2 gap-4 md:flex-1 md:overflow-hidden">
              {/* Left: product grid */}
              <div className="md:overflow-y-auto pr-2">
                <div className="sticky top-0 bg-white pb-2 z-10 space-y-2 mb-1">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground"/>
                    <Input placeholder="Buscar..." value={q} onChange={e=>setQ(e.target.value)} className="pl-8 h-9" data-testid="tab-prod-search"/>
                  </div>
                  {catChips.length > 0 && (
                    <div className="flex gap-1.5 overflow-x-auto pb-0.5" data-testid="tab-cat-chips">
                      {[{ id: "all", name: "Todas", count: products.length }, ...catChips].map(c => (
                        <button key={c.id} type="button" onClick={()=>setCatFilter(c.id)}
                          aria-pressed={catFilter === c.id}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition ${
                            catFilter === c.id ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white text-neutral-700 hover:bg-neutral-50"}`}
                          data-testid={`tab-cat-${c.id}`}>
                          {c.name} <span className="opacity-60">{c.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {filtered.map(p=>{
                    const hasMods = Array.isArray(p.modifier_groups) && p.modifier_groups.length > 0;
                    const dest = printDests(p).join(" + ");
                    const destBadge = { cozinha:"🍳", bar:"🍺", atendimento:"☕" }[printDests(p)[0]] || "🍳";
                    return (
                      <button key={p.id} onClick={()=>onProductClick(p)}
                        className="text-left p-3 border rounded-lg hover:bg-emerald-50 hover:border-emerald-400 transition"
                        data-testid={`tab-prod-${p.id}`}>
                        <div className="font-medium text-sm truncate">{p.name}</div>
                        <div className="text-xs text-emerald-700 font-semibold">{brl(p.price)}</div>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap text-[10px]">
                          {p.no_print
                            ? <span className="text-neutral-500">sem impressão</span>
                            : <span className="text-neutral-500" title={`Imprime na comandeira ${dest}`}>{destBadge} {dest}</span>}
                          {p.has_tip && <span className="text-amber-600">💵</span>}
                          {hasMods && <span className="text-indigo-600 font-medium">✦</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right: items + checkout */}
              <div className="flex flex-col md:border-l md:pl-4 md:overflow-hidden" data-testid="tab-cart-panel">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">Consumo ({t.items.length})</h4>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const pendingCount = t.items.filter(i => !i.paid && (i.print_status || "pending") === "pending").length;
                      return pendingCount > 0 ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={sendToPrint}
                          className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          data-testid="tab-send-to-print">
                          <Printer className="w-3 h-3 mr-1"/> Enviar p/ impressão ({pendingCount})
                        </Button>
                      ) : null;
                    })()}
                    <TabStatusBadge status={t.status} />
                  </div>
                </div>
                <div className="md:flex-1 md:overflow-y-auto space-y-1 mb-3" data-testid="tab-items-list">
                  {t.items.map(i=>(
                    <div key={i.id}
                         className={`flex items-start justify-between gap-2 py-1.5 border-b text-sm ${i.paid ? "bg-emerald-50/60 opacity-70" : ""}`}
                         data-testid={`tab-item-${i.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium truncate ${i.paid ? "line-through" : ""}`}>{i.quantity}x {i.name}</div>
                        {Array.isArray(i.modifiers) && i.modifiers.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`tab-item-mods-${i.id}`}>
                            {i.modifiers.map((m, idx) => (
                              <span key={idx} className="inline-flex items-center text-[10px] bg-indigo-50 text-indigo-800 border border-indigo-200 rounded px-1.5 py-0.5">
                                {m.group}: <b className="ml-1">{m.option}</b>
                                {m.price_delta > 0 && <span className="ml-1 text-emerald-700">+{brl(m.price_delta)}</span>}
                              </span>
                            ))}
                          </div>
                        )}
                        {i.notes && (
                          <div className="text-[11px] italic text-amber-700 mt-0.5" data-testid={`tab-item-notes-${i.id}`}>📝 {i.notes}</div>
                        )}
                        <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                          <span>{new Date(i.added_at).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}</span>
                          {(() => {
                            const destBadge = printDests(i)
                              .map(d => ({ cozinha:"🍳 cozinha", bar:"🍺 bar", atendimento:"☕ atendimento" }[d] || d))
                              .join(" + ");
                            const isSent = i.print_status === "sent";
                            if (i.print_status === "none") {
                              return <span className="text-neutral-500" data-testid={`tab-item-print-${i.id}`}>sem impressão</span>;
                            }
                            return (
                              <span className={`inline-flex items-center gap-0.5 ${isSent ? "text-emerald-700" : "text-amber-700"}`} data-testid={`tab-item-print-${i.id}`}>
                                <Printer className="w-2.5 h-2.5"/>
                                {destBadge}
                                {isSent ? " ✓" : " · pendente"}
                              </span>
                            );
                          })()}
                          {i.paid && <span className="text-emerald-700 font-semibold">✓ pago</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{brl(i.subtotal)}</div>
                      </div>
                      {!i.paid && !offline && !isOfflineId && (
                        <button onClick={()=>removeItem(i.id)} className="text-rose-600 hover:bg-rose-50 p-1 rounded"><Trash2 className="w-3 h-3"/></button>
                      )}
                    </div>
                  ))}
                  {t.items.length===0 && <p className="text-xs text-muted-foreground text-center py-6">Adicione produtos pela lista à esquerda.</p>}
                </div>

                {/* Close panel — finalization is blocked here by design */}
                <div className="border-t pt-3 space-y-2 bg-white">
                  <div className="flex justify-between text-sm">
                    <span>Total</span>
                    <span className="font-semibold tabular-nums">{brl(t.total)}</span>
                  </div>
                  {(t.paid_amount || 0) > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-emerald-700" data-testid="tab-paid-row">
                        <span>Pago</span>
                        <span className="font-semibold tabular-nums">{brl(t.paid_amount)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold" data-testid="tab-remaining-row">
                        <span>Restante</span>
                        <span className="text-amber-600 tabular-nums">{brl(t.remaining_amount || 0)}</span>
                      </div>
                    </>
                  )}
                  {!cashOpen && (
                    <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-start gap-2"
                         data-testid="tab-cash-closed-warning">
                      <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <b>Caixa fechado.</b> Abra o caixa para poder finalizar comandas.
                      </div>
                    </div>
                  )}
                  {t.status === "paid" ? (
                    <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm flex items-center gap-2">
                      ✓ Comanda 100% paga
                    </div>
                  ) : (isOfflineId || offline) ? (
                    <div className="p-2.5 rounded-lg bg-red-50 border border-red-200 text-red-900 text-xs flex items-start gap-2" data-testid="tab-offline-note">
                      <WifiOff className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <b>Comanda offline.</b> Adicione itens agora. O fechamento estará disponível no Caixa quando a rede voltar.
                      </div>
                    </div>
                  ) : (
                    <>
                      <Button
                        onClick={() => setSplitOpen(true)}
                        disabled={!cashOpen || t.items.filter(i => !i.paid).length === 0}
                        className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 h-11"
                        data-testid="tab-split-pay-btn">
                        <Split className="w-4 h-4 mr-1" />
                        Dividir & receber pagamento
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setConferenciaOpen(true)}
                              disabled={t.items.filter(i => !i.paid).length === 0}
                              className="w-full" data-testid="tab-conferencia-btn">
                        <Receipt className="w-3.5 h-3.5 mr-1" /> Imprimir conferência
                      </Button>
                      <Button variant="outline" size="sm" onClick={goToCash}
                              disabled={t.items.length === 0 || t.status === "partial"}
                              className="w-full" data-testid="tab-go-to-cash-btn">
                        <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Finalizar no Caixa
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setMergeOpen(true)}
                              className="w-full" data-testid="tab-merge-btn">
                        <Merge className="w-3.5 h-3.5 mr-1" /> Agrupar comandas
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <TabSplitPayDialog
              open={splitOpen}
              onClose={() => setSplitOpen(false)}
              tab={t}
              onPaid={() => { load(); }}
              fiscalConfig={fiscalConfig}
              userRole={userRole}
              tipDefaultPercent={tipDefaultPercent}
            />
            <ConferenciaDialog
              open={conferenciaOpen}
              onClose={() => setConferenciaOpen(false)}
              tab={t}
            />
            <MergeTabsDialog
              open={mergeOpen}
              onClose={() => setMergeOpen(false)}
              tab={t}
              onMerged={() => { setMergeOpen(false); load(); }}
            />
            {customizeFor && (
              <ItemCustomizeDialog
                product={customizeFor}
                onCancel={() => setCustomizeFor(null)}
                onConfirm={addCustomized}
              />
            )}
      </DialogContent>
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md" data-testid="tab-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancelar comanda {t ? `"${t.label}"` : ""}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Esta ação não pode ser desfeita. Informe um motivo para o registro (opcional).</p>
            <div>
              <Label>Motivo</Label>
              <Select value={cancelReason || "none"} onValueChange={v=>setCancelReason(v==="none"?"":v)}>
                <SelectTrigger data-testid="cancel-reason-select"><SelectValue placeholder="Selecionar motivo..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não informar</SelectItem>
                  <SelectItem value="Cliente desistiu">Cliente desistiu</SelectItem>
                  <SelectItem value="Erro no pedido">Erro no pedido</SelectItem>
                  <SelectItem value="Item indisponível">Item indisponível</SelectItem>
                  <SelectItem value="Duplicada">Comanda duplicada</SelectItem>
                  <SelectItem value="Outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {cancelReason === "Outro" && (
              <div>
                <Label>Descreva</Label>
                <Input autoFocus maxLength={200} placeholder="Descreva o motivo..."
                  onChange={e=>setCancelReason(e.target.value)} data-testid="cancel-reason-input" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Button variant="outline" className="rounded-full" onClick={()=>setCancelOpen(false)} data-testid="cancel-abort">
                Voltar
              </Button>
              <Button disabled={cancelling} onClick={confirmCancel} className="rounded-full bg-rose-600 hover:bg-rose-700" data-testid="cancel-confirm">
                {cancelling ? "Cancelando..." : "Confirmar cancelamento"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}


// =============== MERGE TABS DIALOG ===============
// Juntar a conta de outras mesas nesta. Caso real: um cliente resolve pagar a
// conta dele e a de mais alguém. Sem isto, o balcão fecha duas vendas
// separadas e perde a informação de que foi uma conta só — ou relança os
// itens à mão na outra comanda.
function MergeTabsDialog({ open, onClose, tab, onMerged }) {
  const [outras, setOutras] = useState([]);
  const [escolhidas, setEscolhidas] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEscolhidas([]);
    api.get("/tabs?status=open")
      .then(r => setOutras((r.data || []).filter(o => o.id !== tab.id)))
      .catch(() => setOutras([]));
  }, [open, tab.id]);

  const alternar = (id) =>
    setEscolhidas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const total = outras
    .filter(o => escolhidas.includes(o.id))
    .reduce((soma, o) => soma + (o.total || 0), 0) + (tab.total || 0);

  const confirmar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tabs/${tab.id}/merge`, { source_tab_ids: escolhidas });
      toast.success(
        `${data.merged.map(m => m.label).join(", ")} agrupada(s) em ${tab.label}: ${brl(data.total)}`
      );
      onMerged();
    } catch (e) {
      // O backend devolve mensagem pronta para origem com pagamento e para
      // comanda que fechou no meio; mostrar ela é mais útil que um erro seco.
      errToast(e, "Não foi possível agrupar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="tab-merge-dialog">
        <DialogHeader><DialogTitle>Agrupar em {tab.label}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          Os itens das comandas marcadas passam para esta, e a conta vira uma só.
          Comanda que já tem pagamento registrado não pode ser agrupada.
        </p>
        <div className="max-h-72 overflow-y-auto space-y-1.5">
          {outras.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Nenhuma outra comanda aberta.
            </p>
          )}
          {outras.map(o => (
            <label key={o.id}
                   className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer ${
                     escolhidas.includes(o.id) ? "border-primary bg-primary-muted" : ""}`}
                   data-testid={`merge-source-${o.id}`}>
              <Checkbox checked={escolhidas.includes(o.id)} onCheckedChange={() => alternar(o.id)} />
              <span className="flex-1 font-medium text-sm">{o.label}</span>
              <span className="text-sm text-muted-foreground">{brl(o.total || 0)}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between border-t pt-3">
          <div>
            <div className="text-xs text-muted-foreground">Total agrupado</div>
            <div className="text-xl font-bold text-primary">{brl(total)}</div>
          </div>
          <Button onClick={confirmar} disabled={busy || escolhidas.length === 0}
                  data-testid="merge-confirm">
            {busy ? "Agrupando..." : `Agrupar (${escolhidas.length})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// =============== TAB STATUS BADGE ===============
function TabStatusBadge({ status }) {
  const map = {
    open:              { label: "Aberta",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    partial:           { label: "Parcial",      cls: "bg-amber-50 text-amber-800 border-amber-300" },
    awaiting_cashier:  { label: "Aguard. caixa", cls: "bg-amber-100 text-amber-900 border-amber-400 font-semibold" },
    paid:              { label: "Paga",         cls: "bg-violet-50 text-violet-700 border-violet-200" },
    cancelled:         { label: "Cancelada",    cls: "bg-rose-50 text-rose-700 border-rose-200" },
    // Agrupada em outra comanda. NÃO é cancelada: o consumo foi pago, só que
    // na conta de quem levou os itens.
    merged:            { label: "Agrupada",     cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  };
  const m = map[status] || map.open;
  return <Badge variant="outline" className={m.cls} data-testid={`tab-status-${status}`}>{m.label}</Badge>;
}

// =============== TAB SPLIT PAY DIALOG ===============
function TabSplitPayDialog({ open, onClose, tab, onPaid, fiscalConfig, userRole = "owner", tipDefaultPercent = 10 }) {
  const isTabStaff = userRole === "staff";
  const userBrands = JSON.parse(localStorage.getItem("oplyx_user") || "{}")?.settings?.card_brands || [];
  const BRANDS = ["visa", "master", "elo", "amex", "hipercard", "outro", ...userBrands];
  const [selected, setSelected] = useState({});
  const [parciais, setParciais] = useState({});
  const [tipMode, setTipMode] = useState("none");
  const [tipPct, setTipPct] = useState(tipDefaultPercent);
  const [tipFixed, setTipFixed] = useState(0);
  const [tipConsent, setTipConsent] = useState(false);
  const [method, setMethod] = useState("cash");
  const [cardKind, setCardKind] = useState("credit");
  const [cardBrand, setCardBrand] = useState("visa");
  const [payerLabel, setPayerLabel] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastSaleId, setLastSaleId] = useState(null);
  // NFC-e após pagamento total
  const [nfceDoc, setNfceDoc] = useState(null);
  const [tabPaid, setTabPaid] = useState(false);
  // Estado da reconsulta manual da NFC-e. Fica AQUI, junto dos outros hooks:
  // o `if (!tab) return null` mais abaixo é um retorno antecipado, e declarar
  // um useState depois dele muda a ordem dos hooks entre renders.
  const [nfceRechecking, setNfceRechecking] = useState(false);
  const [nfceEmitting, setNfceEmitting]     = useState(false);
  const [nfceError, setNfceError]           = useState(null);
  const _autoPrintedNfce = useRef(null);

  // Emissão da NFC-e da venda que acabou de ser cobrada.
  // O PDV faz exatamente esta chamada depois de cada venda; aqui ela faltava, e
  // o fechamento pela comanda ficava dependendo do `auto_emit` do backend — num
  // tenant que emite sob demanda a nota simplesmente nunca aparecia para
  // impressão. O endpoint é idempotente (devolve `already_exists` quando a
  // auto-emissão já criou o documento), então chamar sempre é seguro.
  const emitNfceDaVenda = async (saleId) => {
    if (!saleId || !fiscalConfig?.nfce_disponivel) return;
    setNfceEmitting(true);
    setNfceError(null);
    try {
      const { data } = await api.post("/fiscal/nfce", { sale_id: saleId });
      const doc = data.document ?? data;
      if (doc?.status) setNfceDoc(doc);
      if (doc?.status === "authorized")    toast.success("NFC-e autorizada pela SEFAZ");
      else if (doc?.status === "rejected") toast.error(`NFC-e rejeitada: ${doc.rejection_reason || "erro desconhecido"}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (typeof d === "string" ? d : d?.message) || "Erro ao emitir NFC-e";
      setNfceError(msg);
      toast.error(msg);
    } finally {
      setNfceEmitting(false);
    }
  };

  const autoPrint = async (saleId) => {
    if (!saleId) return;
    try {
      const { data: agentStatus } = await api.get("/print-agent/status");
      if (agentStatus?.online) return;
    } catch { /* sem agente — segue para fallback */ }
    try {
      const printer = await getBranchPrinter("caixa");
      if (printer && await isQZAvailable()) {
        const [{ data: saleData }, biz] = await Promise.all([
          api.get(`/sales/${saleId}`),
          Promise.resolve(JSON.parse(localStorage.getItem("oplyx_user") || "{}")),
        ]);
        await qzPrintReceipt(saleData, biz, printer);
        return;
      }
    } catch (e) {
      console.warn("[auto-print] QZ Tray falhou:", e.message);
    }
    window.open(`/receipt/${saleId}?auto=1`, "receipt", "width=380,height=640");
  };

  // Polling NFC-e de cada venda cobrada (fração ou fechamento total).
  // Não espera a comanda ficar 100% paga: cada fração é uma venda real, com
  // NFC-e própria, e o consumidor que pagou a parte dele leva a nota dele.
  useEffect(() => {
    if (!lastSaleId || !fiscalConfig?.nfce_disponivel) return;
    if (nfceDoc && nfceDoc.status !== "pending") return;

    const startMs = Date.now();
    const MAX_MS  = 60_000;
    let attempt   = 0;

    const poll = async () => {
      attempt++;
      try {
        const doc = await fetchNfceBySale(lastSaleId);
        setNfceDoc(doc);
        if (doc.status !== "pending") return;
      } catch { /* ignora erros de rede */ }
      if (Date.now() - startMs >= MAX_MS) {
        setNfceDoc(prev => prev ? { ...prev, _timedOut: true } : { status: "pending", _timedOut: true });
        return;
      }
      timer = setTimeout(poll, attempt < 4 ? 3_000 : 6_000);
    };

    let timer = setTimeout(poll, 2_000);
    return () => clearTimeout(timer);
  }, [lastSaleId, nfceDoc?.id, nfceDoc?.status, fiscalConfig]); // eslint-disable-line

  // Auto-print NFC-e: abre o cupom quando autorizada (ou emitida em contingência)
  useEffect(() => {
    if (!NFCE_IMPRIMIVEL.has(nfceDoc?.status) || !nfceDoc.id) return;
    if (_autoPrintedNfce.current === nfceDoc.id) return;
    _autoPrintedNfce.current = nfceDoc.id;
    openNfceCupom(nfceDoc.id, "danfe_print", "width=800,height=700");
  }, [nfceDoc?.status, nfceDoc?.id]); // eslint-disable-line

  const loadHistory = () => api.get(`/tabs/${tab.id}/payments`).then(r => setHistory(r.data?.items || []));
  useEffect(() => {
    if (open && tab?.id) {
      setSelected({}); setTipMode("none"); setTipPct(10); setTipFixed(0);
      setTipConsent(false); setMethod("cash"); setPayerLabel("");
      setLastSaleId(null); setNfceDoc(null); setTabPaid(false);
      setNfceEmitting(false); setNfceError(null);
      _autoPrintedNfce.current = null;
      loadHistory();
    }
    /* eslint-disable-next-line */
  }, [open, tab?.id]);

  if (!tab) return null;
  const unpaid = (tab.items || []).filter(i => !i.paid);
  const selIds = Object.keys(selected).filter(k => selected[k]);
  const selItems = unpaid.filter(i => selIds.includes(i.id));
  // Quantas unidades de cada item entram nesta fração. Ausente = todas.
  // "4x sanduíche" é UM item e `paid` é sim ou não: dois amigos que querem
  // pagar dois cada precisam separar as unidades antes (POST .../split).
  const parteDoItem = (i) => {
    const q = parciais[i.id] ?? i.quantity;
    return q >= i.quantity ? (i.subtotal || 0) : +(((i.subtotal || 0) / i.quantity) * q).toFixed(2);
  };
  const subtotal = +selItems.reduce((s, i) => s + parteDoItem(i), 0).toFixed(2);
  const tipEligibleItems = selItems.filter(i => i.has_tip);
  const tipEligibleSubtotal = +tipEligibleItems.reduce((s, i) => s + parteDoItem(i), 0).toFixed(2);
  const hasEligible = tipEligibleSubtotal > 0;
  // TETO DE 10% — mesma regra do backend (backend/tip_lib.py). O limite anterior
  // para valor fixo era o subtotal elegível INTEIRO, ou seja, 100%: não era
  // teto. Arredonda para baixo, senão não é teto. Aqui não há desconto — o
  // desconto só existe no fechamento da comanda, que passa pelo PDV.
  const tipCap = Math.floor(Math.min(tipEligibleSubtotal, subtotal) * GORJETA_TETO_PCT) / 100;
  const tipValue = (tipMode === "none" || !tipConsent || !hasEligible) ? 0
    : tipMode === "fixed" ? +Math.min(parseFloat(tipFixed) || 0, tipCap).toFixed(2)
    : +Math.min(tipEligibleSubtotal * Math.min(parseFloat(tipPct) || 0, GORJETA_TETO_PCT) / 100, tipCap).toFixed(2);
  const total = +(subtotal + tipValue).toFixed(2);

  const toggleAll = () => {
    if (selIds.length === unpaid.length) setSelected({});
    else setSelected(Object.fromEntries(unpaid.map(i => [i.id, true])));
  };

  const ajustarParcial = (item, delta) => {
    setParciais(prev => {
      const atual = prev[item.id] ?? item.quantity;
      return { ...prev, [item.id]: Math.min(item.quantity, Math.max(1, atual + delta)) };
    });
    setSelected(s => ({ ...s, [item.id]: true }));
  };

  const submit = async () => {
    if (selItems.length === 0) return toast.error("Selecione pelo menos um item");
    if (tipMode !== "none" && !tipConsent && hasEligible) {
      return toast.error("Confirme com o cliente antes de aplicar a gorjeta");
    }
    setBusy(true);
    try {
      const includeTip = tipMode !== "none" && tipConsent && hasEligible;
      // Separa as unidades antes de cobrar: é a linha nova que entra na
      // fração. Se algo falhar aqui, ninguém pagou nada e a comanda só fica
      // com o item repartido — o total não muda.
      const idsParaCobrar = [];
      for (const i of selItems) {
        const parte = parciais[i.id] ?? i.quantity;
        if (parte >= i.quantity) { idsParaCobrar.push(i.id); continue; }
        const { data: sp } = await api.post(`/tabs/${tab.id}/items/${i.id}/split`, { quantity: parte });
        idsParaCobrar.push(sp.new_item_id);
      }
      const payload = {
        item_ids: idsParaCobrar,
        ...(includeTip && tipMode === "percent" ? { tip_percent: parseFloat(tipPct) || 0 } : {}),
        ...(includeTip && tipMode === "fixed" ? { tip_amount: parseFloat(tipFixed) || 0 } : {}),
        payment_method: method,
        ...(method === "card" ? { card_kind: cardKind, card_brand: cardBrand } : {}),
        payer_label: payerLabel.trim() || null,
      };
      const { data } = await api.post(`/tabs/${tab.id}/pay-fraction`, payload);
      toast.success(`Recebido ${brl(data.payment.total)}${data.tab.status === "paid" ? ". Comanda 100% paga" : ""}`);
      onPaid();
      loadHistory();
      setSelected({});
      setParciais({});
      setTipConsent(false);
      if (data.sale?.id) {
        setLastSaleId(data.sale.id);
        setNfceDoc(null);
        setNfceError(null);
        emitNfceDaVenda(data.sale.id);
      }
      if (data.tab.status === "paid") {
        setTabPaid(true);
        if (data.sale?.id) autoPrint(data.sale.id);
        // Não fecha imediatamente — mostra status NFC-e se configurado
        if (!fiscalConfig?.nfce_disponivel) onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar pagamento");
    } finally { setBusy(false); }
  };

  const openNfcePdf = () => {
    if (!nfceDoc?.id) return;
    openNfceCupom(nfceDoc.id);
  };

  // Reconsulta manual: o polling automático para após 60s sem resposta (_timedOut).
  // A emissão roda em background no backend (fire-and-forget) e o reconciliador
  // periódico pode autorizar a NFC-e minutos depois — sem isso o operador ficava
  // sem nenhuma ação disponível para checar/imprimir a nota da venda.
  // (o useState correspondente é declarado junto dos demais hooks, acima do
  //  retorno antecipado `if (!tab) return null`)
  const recheckNfce = async () => {
    if (!lastSaleId || nfceRechecking) return;
    setNfceRechecking(true);
    try {
      const doc = await fetchNfceBySale(lastSaleId);
      setNfceDoc({ ...doc, _timedOut: doc.status === "pending" ? true : undefined });
      if (doc.status === "authorized") toast.success("NFC-e autorizada pela SEFAZ");
      else if (doc.status === "pending") toast.info("Ainda aguardando autorização da SEFAZ");
    } catch {
      toast.error("Erro ao consultar status da NFC-e");
    } finally {
      setNfceRechecking(false);
    }
  };

  // ── NFC-e status badge ──────────────────────────────────────────────────────
  const NfceBadge = () => {
    if (!fiscalConfig?.nfce_disponivel) return null;
    if (!lastSaleId) return null;

    if (nfceError && !nfceDoc) {
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            <AlertTriangle className="w-3.5 h-3.5" /> {nfceError}
          </span>
          <Button size="sm" variant="outline" onClick={() => emitNfceDaVenda(lastSaleId)}
                  disabled={nfceEmitting} className="gap-1.5 h-7 text-xs"
                  data-testid="split-nfce-retry">
            {nfceEmitting
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Emitindo…</>
              : <><RefreshCw className="w-3.5 h-3.5" /> Tentar novamente</>}
          </Button>
        </div>
      );
    }

    if (!nfceDoc) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Emitindo NFC-e…
        </div>
      );
    }

    if (NFCE_IMPRIMIVEL.has(nfceDoc.status)) {
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {nfceDoc.status === "contingency"
              ? "NFC-e emitida em contingência, com transmissão automática à SEFAZ"
              : "NFC-e autorizada pela SEFAZ"}
          </span>
          {nfceDoc.id && (
            <Button size="sm" variant="outline" onClick={openNfcePdf}
                    className="gap-1.5 h-7 text-xs" data-testid="split-nfce-print">
              <FileText className="w-3.5 h-3.5" /> Imprimir NFC-e
            </Button>
          )}
        </div>
      );
    }

    if (nfceDoc.status === "rejected" || nfceDoc.status === "error") {
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            NFC-e rejeitada: {typeof nfceDoc.rejection_reason === "string"
              ? nfceDoc.rejection_reason
              : nfceDoc.rejection_reason != null
                ? JSON.stringify(nfceDoc.rejection_reason)
                : "erro desconhecido"}
          </span>
          <Button size="sm" variant="outline" onClick={() => emitNfceDaVenda(lastSaleId)}
                  disabled={nfceEmitting} className="gap-1.5 h-7 text-xs"
                  data-testid="split-nfce-retry-rejected">
            {nfceEmitting
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Emitindo…</>
              : <><RefreshCw className="w-3.5 h-3.5" /> Tentar novamente</>}
          </Button>
        </div>
      );
    }

    if (nfceDoc._timedOut) {
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded px-2 py-1">
            <FileText className="w-3.5 h-3.5" /> SEFAZ ainda não respondeu. A nota pode ser autorizada em instantes.
          </span>
          <Button size="sm" variant="outline" onClick={recheckNfce} disabled={nfceRechecking}
                  className="gap-1.5 h-7 text-xs" data-testid="tab-nfce-recheck">
            {nfceRechecking
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Verificando…</>
              : <><RefreshCw className="w-3.5 h-3.5" /> Verificar / imprimir NFC-e</>}
          </Button>
        </div>
      );
    }

    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Aguardando SEFAZ…
      </span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tab-split-dialog">
        <DialogHeader><DialogTitle>Dividir e receber: {tab.label}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          {/* Left: items selector */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-sm">Itens disponíveis ({unpaid.length})</h4>
              <Button size="sm" variant="ghost" onClick={toggleAll} data-testid="split-toggle-all">
                {selIds.length === unpaid.length ? "Limpar" : "Selecionar todos"}
              </Button>
            </div>
            <div className="space-y-1 max-h-80 overflow-y-auto border rounded p-2">
              {unpaid.length === 0 && <div className="text-xs text-muted-foreground py-4 text-center">Todos os itens já foram pagos.</div>}
              {unpaid.map(it => (
                <label key={it.id} className="flex items-center gap-2 p-2 rounded hover:bg-neutral-50 cursor-pointer text-sm"
                       data-testid={`split-item-${it.id}`}>
                  <input type="checkbox" checked={!!selected[it.id]}
                         onChange={(e) => setSelected(s => ({ ...s, [it.id]: e.target.checked }))} />
                  <div className="flex-1">
                    <div>{it.quantity}x {it.name}</div>
                    {it.quantity > 1 && selected[it.id] && (
                      <div className="flex items-center gap-2 mt-1" onClick={(e) => e.preventDefault()}>
                        <Button type="button" size="icon" variant="outline" className="h-6 w-6"
                                onClick={() => ajustarParcial(it, -1)}
                                data-testid={`split-part-minus-${it.id}`}>−</Button>
                        <span className="text-[11px] font-semibold text-primary">
                          pagar {parciais[it.id] ?? it.quantity} de {it.quantity}
                        </span>
                        <Button type="button" size="icon" variant="outline" className="h-6 w-6"
                                onClick={() => ajustarParcial(it, +1)}
                                data-testid={`split-part-plus-${it.id}`}>+</Button>
                      </div>
                    )}
                  </div>
                  <div className="font-semibold tabular-nums">{brl(parteDoItem(it))}</div>
                </label>
              ))}
            </div>
          </div>
          {/* Right: payer details */}
          <div className="space-y-3">
            <div>
              <Label>Pagador (opcional)</Label>
              <Input value={payerLabel} onChange={e => setPayerLabel(e.target.value)}
                     placeholder="Nome do pagador" data-testid="split-payer-label" />
            </div>
            <div>
              <Label>Gorjeta <span className="text-[10px] font-normal text-muted-foreground">(opcional, confirme com o cliente)</span></Label>
              <div className="flex gap-2 mt-1">
                {isTabStaff ? (
                  /* Staff: apenas sem gorjeta ou % padrão configurada (sem campo livre) */
                  <>
                    <button type="button" onClick={()=>{ setTipMode("none"); setTipConsent(false); }}
                      className={`px-3 h-9 rounded-full text-xs font-medium border transition ${tipMode==="none"?"bg-amber-500 text-white border-amber-500":"bg-white hover:bg-amber-50"}`}
                      data-testid="tip-pct-0">
                      Sem gorjeta
                    </button>
                    <button type="button" onClick={()=>{ setTipMode("percent"); setTipPct(tipDefaultPercent); }}
                      className={`px-3 h-9 rounded-full text-xs font-medium border transition ${tipMode==="percent"?"bg-amber-500 text-white border-amber-500":"bg-white hover:bg-amber-50"}`}
                      data-testid={`tip-pct-${tipDefaultPercent}`}>
                      {tipDefaultPercent}% (padrão)
                    </button>
                  </>
                ) : (
                  /* Owner/Manager: select completo + campo livre */
                  <>
                    <Select value={tipMode} onValueChange={(v)=>{ setTipMode(v); if(v==="none") setTipConsent(false); }}>
                      <SelectTrigger className="w-32" data-testid="split-tip-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem gorjeta</SelectItem>
                        <SelectItem value="percent">Percentual</SelectItem>
                        <SelectItem value="fixed">Valor fixo</SelectItem>
                      </SelectContent>
                    </Select>
                    {tipMode === "percent" && (
                      <Input type="number" min="0" max={GORJETA_TETO_PCT} step="1" className="w-20 text-right"
                             value={tipPct}
                             onChange={e => setTipPct(String(Math.min(parseFloat(e.target.value) || 0, GORJETA_TETO_PCT)))}
                             data-testid="split-tip-pct" />
                    )}
                    {tipMode === "fixed" && (
                      <Input type="number" min="0" step="0.01" className="w-28 text-right"
                             value={tipFixed} onChange={e => setTipFixed(e.target.value)} data-testid="split-tip-fixed" />
                    )}
                  </>
                )}
              </div>
              {tipMode !== "none" && (
                <div className="mt-2 space-y-1.5">
                  {selItems.length > 0 && (
                    <div className="text-[11px] text-muted-foreground" data-testid="tip-eligible-info">
                      {hasEligible ? (
                        <>Base elegível: <b className="tabular-nums">{brl(tipEligibleSubtotal)}</b> · {tipEligibleItems.length} de {selItems.length} item(s) aceitam gorjeta</>
                      ) : (
                        <span className="text-amber-700">Nenhum item selecionado aceita gorjeta (cadastro do produto). A gorjeta será ignorada.</span>
                      )}
                    </div>
                  )}
                  {hasEligible && (
                    <label className="flex items-start gap-2 text-xs text-neutral-700 bg-amber-50 border border-amber-200 rounded p-2 cursor-pointer" data-testid="tip-consent-row">
                      <input type="checkbox" checked={tipConsent}
                             onChange={e => setTipConsent(e.target.checked)}
                             className="mt-0.5"
                             data-testid="split-tip-consent" />
                      <span>Cliente <b>concordou</b> com a gorjeta de <b className="tabular-nums">{brl(tipValue)}</b>.</span>
                    </label>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label>Forma de pagamento</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="split-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Dinheiro</SelectItem>
                  <SelectItem value="pix">Pix</SelectItem>
                  <SelectItem value="card">Cartão</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {method === "card" && (
              <div className="grid grid-cols-2 gap-2" data-testid="split-card-detail">
                <Select value={cardKind} onValueChange={setCardKind}>
                  <SelectTrigger data-testid="split-card-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">Crédito</SelectItem>
                    <SelectItem value="debit">Débito</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={cardBrand} onValueChange={setCardBrand}>
                  <SelectTrigger data-testid="split-card-brand"><SelectValue /></SelectTrigger>
                  <SelectContent>{BRANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{brl(subtotal)}</span></div>
              <div className="flex justify-between"><span>Gorjeta</span><span className="tabular-nums">{brl(tipValue)}</span></div>
              <div className="flex justify-between text-base font-bold pt-1 border-t"><span>Total</span><span className="tabular-nums" data-testid="split-total">{brl(total)}</span></div>
            </div>
          </div>
        </div>
        {/* History */}
        {history.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <h4 className="font-semibold text-sm mb-2">Pagamentos da comanda ({history.length})</h4>
            <div className="space-y-1 text-xs" data-testid="split-history">
              {history.map(h => (
                <div key={h.id} className="flex justify-between py-1 border-b last:border-0">
                  <div>
                    <b>{h.payer_label || "—"}</b>
                    <span className="text-muted-foreground ml-2">
                      {h.payment_method}{h.card_brand ? ` ${h.card_kind}/${h.card_brand}` : ""}
                    </span>
                    <span className="text-muted-foreground ml-2">· {h.items?.length} item(s)</span>
                  </div>
                  <div>{brl(h.total)} <span className="text-[10px] text-muted-foreground">+{brl(h.tip_amount)} tip</span></div>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2 items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <NfceBadge />
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" onClick={onClose}>Fechar</Button>
            {lastSaleId && !tabPaid && (
              <Button variant="outline" onClick={() => autoPrint(lastSaleId)}
                      className="gap-1.5" data-testid="split-print-receipt">
                <Printer className="w-4 h-4" /> Imprimir recibo
              </Button>
            )}
            <Button onClick={submit} disabled={busy || selItems.length === 0 || tabPaid}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="split-submit">
              {busy ? "Registrando..." : tabPaid ? "Pago ✓" : `Receber ${brl(total)}`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =============== ITEM CUSTOMIZE DIALOG ===============
// Diálogo para adicionar um produto à comanda com:
//  - quantidade (default 1, suporta decimais para vendas a peso)
//  - escolha de modificadores (radio para single, checkbox para multi)
//  - observação livre por item (ex.: "ovo mexido com gema dura")
function ItemCustomizeDialog({ product, onCancel, onConfirm }) {
  const groups = Array.isArray(product.modifier_groups) ? product.modifier_groups : [];
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [selected, setSelected] = useState(() => {
    const out = {};
    groups.forEach((g) => { out[g.name] = []; });
    return out;
  });

  const toggleOption = (g, optLabel) => {
    setSelected((prev) => {
      const cur = prev[g.name] || [];
      if (g.multi) {
        return { ...prev, [g.name]: cur.includes(optLabel) ? cur.filter((x) => x !== optLabel) : [...cur, optLabel] };
      }
      return { ...prev, [g.name]: cur[0] === optLabel ? [] : [optLabel] };
    });
  };

  const deltaPerUnit = groups.reduce((sum, g) => {
    const chosen = selected[g.name] || [];
    chosen.forEach((label) => {
      const o = (g.options || []).find((x) => x.label === label);
      if (o) sum += parseFloat(o.price_delta) || 0;
    });
    return sum;
  }, 0);
  const unitPrice = (parseFloat(product.price) || 0) + deltaPerUnit;
  const subtotal = unitPrice * (parseFloat(qty) || 0);

  const missing = groups.filter((g) => g.required && (selected[g.name] || []).length === 0).map((g) => g.name);
  const canSubmit = missing.length === 0 && (parseFloat(qty) || 0) > 0;

  const submit = () => {
    if (!canSubmit) return;
    const modifiers = [];
    groups.forEach((g) => {
      (selected[g.name] || []).forEach((label) => {
        const o = (g.options || []).find((x) => x.label === label);
        modifiers.push({ group: g.name, option: label, price_delta: parseFloat(o?.price_delta) || 0 });
      });
    });
    onConfirm({
      product_id: product.id,
      quantity: parseFloat(qty) || 1,
      notes: notes.trim() || null,
      ...(modifiers.length ? { modifiers } : {}),
    });
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="item-customize-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-indigo-600" />
            Personalizar: {product.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          <div className="flex items-center gap-3">
            <Label className="w-24 flex-shrink-0">Quantidade</Label>
            <div className="flex items-center border rounded-lg overflow-hidden">
              <button type="button" onClick={() => setQty(Math.max(0.1, +(parseFloat(qty) - 1).toFixed(2)))}
                      className="px-3 py-2 hover:bg-neutral-100" data-testid="customize-qty-minus">
                <Minus className="w-3 h-3"/>
              </button>
              <input type="number" step="0.1" min="0.1" value={qty}
                     onChange={(e) => setQty(e.target.value)}
                     className="w-16 text-center border-0 focus:outline-none text-sm font-semibold"
                     data-testid="customize-qty"/>
              <button type="button" onClick={() => setQty(+(parseFloat(qty) + 1).toFixed(2))}
                      className="px-3 py-2 hover:bg-neutral-100" data-testid="customize-qty-plus">
                <Plus className="w-3 h-3"/>
              </button>
            </div>
          </div>

          {groups.map((g) => (
            <div key={g.name} className="space-y-1.5" data-testid={`customize-group-${g.name}`}>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold flex items-center gap-1.5">
                  {g.name}
                  {g.required && <span className="text-[10px] text-rose-600 font-normal">obrigatório</span>}
                </Label>
                <span className="text-[10px] text-muted-foreground">
                  {g.multi ? "múltipla escolha" : "uma opção"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {(g.options || []).map((o) => {
                  const isSel = (selected[g.name] || []).includes(o.label);
                  return (
                    <button
                      key={o.label}
                      type="button"
                      onClick={() => toggleOption(g, o.label)}
                      className={`text-left px-3 py-2 border rounded-md text-sm transition ${isSel ? "border-indigo-500 bg-indigo-50 text-indigo-900 font-medium" : "border-neutral-200 hover:border-neutral-300"}`}
                      data-testid={`customize-opt-${g.name}-${o.label}`}>
                      <div className="flex items-center justify-between">
                        <span>{o.label}</span>
                        {parseFloat(o.price_delta) > 0 && (
                          <span className="text-[10px] text-emerald-700 font-semibold">+{brl(parseFloat(o.price_delta))}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div>
            <Label className="text-sm font-semibold">Observação <span className="text-[10px] font-normal text-muted-foreground">(opcional, passa para a cozinha)</span></Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Ex.: ovo mexido com gema dura, sem cebola, ponto da carne ao ponto..."
              className="mt-1 w-full text-sm border rounded-md p-2 resize-y focus:outline-none focus:border-indigo-400"
              data-testid="customize-notes"
            />
          </div>

          {missing.length > 0 && (
            <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded p-2" data-testid="customize-missing">
              Selecione: <b>{missing.join(", ")}</b>
            </p>
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <div className="flex items-center gap-3 w-full">
            <div className="flex-1 text-left">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Subtotal</div>
              <div className="text-lg font-bold tabular-nums" data-testid="customize-subtotal">{brl(subtotal)}</div>
              {deltaPerUnit > 0 && (
                <div className="text-[10px] text-emerald-700">
                  base {brl(parseFloat(product.price))} + {brl(deltaPerUnit)}/un
                </div>
              )}
            </div>
            <Button variant="outline" onClick={onCancel} className="rounded-full" data-testid="customize-cancel">Cancelar</Button>
            <Button onClick={submit} disabled={!canSubmit} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="customize-confirm">
              <Plus className="w-4 h-4 mr-1"/> Adicionar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
